const ApiClient = (function() {
    const DEFAULT_TIMEOUT = 120000;
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 4000];
    
    function generateIdempotencyKey() {
        return 'req_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    function isRetryableError(xhr, status) {
        if (status === 'timeout') return true;
        if (status === 'error' && !xhr.status) return true;
        if (xhr.status === 499) return true;
        if (xhr.status === 502 || xhr.status === 503 || xhr.status === 504) return true;
        
        try {
            const response = JSON.parse(xhr.responseText);
            if (response && response.isRetryable === true) return true;
        } catch (e) {}
        
        return false;
    }

    function delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async function fetchWithRetry(url, options, retryCount = 0) {
        const idempotencyKey = options.idempotencyKey || generateIdempotencyKey();
        
        const headers = {
            'Content-Type': 'application/json; charset=utf-8',
            'X-Idempotency-Key': idempotencyKey,
            'X-Retry-Count': retryCount.toString(),
            ...(options.headers || {})
        };

        try {
            const response = await fetch(url, {
                method: options.method || 'POST',
                headers: headers,
                body: options.body ? JSON.stringify(options.body) : undefined,
                signal: options.signal
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.isRetryable && retryCount < MAX_RETRIES) {
                    console.log(`Retryable error, attempt ${retryCount + 1}/${MAX_RETRIES}`);
                    await delay(RETRY_DELAYS[retryCount] || 4000);
                    return fetchWithRetry(url, { ...options, idempotencyKey }, retryCount + 1);
                }
                throw { response, data };
            }

            return data;
        } catch (error) {
            if (error.name === 'AbortError') {
                throw { message: 'Request was cancelled', isRetryable: false };
            }

            if (retryCount < MAX_RETRIES && !error.response) {
                console.log(`Network error, retrying ${retryCount + 1}/${MAX_RETRIES}`);
                await delay(RETRY_DELAYS[retryCount] || 4000);
                return fetchWithRetry(url, { ...options, idempotencyKey }, retryCount + 1);
            }

            throw error;
        }
    }

    function ajaxWithRetry(options) {
        return new Promise((resolve, reject) => {
            const idempotencyKey = options.idempotencyKey || generateIdempotencyKey();
            let retryCount = 0;

            function makeRequest() {
                $.ajax({
                    url: options.url,
                    type: options.type || 'POST',
                    contentType: options.contentType || 'application/json; charset=utf-8',
                    data: options.data,
                    timeout: options.timeout || DEFAULT_TIMEOUT,
                    headers: {
                        'X-Idempotency-Key': idempotencyKey,
                        'X-Retry-Count': retryCount.toString(),
                        ...(options.headers || {})
                    },
                    beforeSend: options.beforeSend,
                    success: function(result) {
                        if (result && result.isError && result.isRetryable && retryCount < MAX_RETRIES) {
                            console.log(`Retryable error in response, attempt ${retryCount + 1}/${MAX_RETRIES}`);
                            retryCount++;
                            setTimeout(makeRequest, RETRY_DELAYS[retryCount - 1] || 4000);
                            return;
                        }
                        resolve(result);
                    },
                    error: function(xhr, status, error) {
                        if (isRetryableError(xhr, status) && retryCount < MAX_RETRIES) {
                            console.log(`Retryable error (${status}), attempt ${retryCount + 1}/${MAX_RETRIES}`);
                            retryCount++;
                            setTimeout(makeRequest, RETRY_DELAYS[retryCount - 1] || 4000);
                            return;
                        }
                        
                        console.error('Request failed after retries:', { xhr, status, error });
                        reject({ xhr, status, error, retryCount });
                    }
                });
            }

            makeRequest();
        });
    }

    function optimizePayload(payload) {
        if (!payload) return payload;

        if (payload.selectedInvoices && Array.isArray(payload.selectedInvoices)) {
            payload.selectedInvoices = payload.selectedInvoices.map(invoice => ({
                InvoiceID: invoice.InvoiceID,
                InvoiceNumber: invoice.InvoiceNumber,
                AmountDue: invoice.AmountDue,
                AmountDueEditable: invoice.AmountDueEditable,
                Reference: invoice.Reference,
                Date: invoice.Date,
                DueDate: invoice.DueDate,
                Status: invoice.Status,
                EmailAddress: invoice.EmailAddress,
                HasEmailAddress: invoice.HasEmailAddress,
                HasBankDetails: invoice.HasBankDetails,
                BankDetails: invoice.BankDetails,
                Contact: invoice.Contact ? {
                    ContactID: invoice.Contact.ContactID,
                    Name: invoice.Contact.Name,
                    EmailAddress: invoice.Contact.EmailAddress
                } : null
            }));
        }

        return payload;
    }

    function processBatchReportWithRetry(payload, callbacks = {}) {
        const optimizedPayload = optimizePayload(payload);
        const idempotencyKey = generateIdempotencyKey();

        return ajaxWithRetry({
            url: API_ENDPOINTS.PROCESS_BATCH_REPORT,
            type: 'POST',
            data: JSON.stringify(optimizedPayload),
            timeout: 300000,
            idempotencyKey: idempotencyKey,
            headers: {
                'X-Batch-Diagnostics': JSON.stringify({
                    invoiceCount: optimizedPayload.selectedInvoices?.length || 0,
                    paymentCount: optimizedPayload.payments?._Payments?.length || 0,
                    timestamp: new Date().toISOString()
                })
            },
            beforeSend: callbacks.beforeSend
        }).then(result => {
            if (callbacks.success) callbacks.success(result);
            return result;
        }).catch(error => {
            if (callbacks.error) callbacks.error(error.xhr, error.status, error.error);
            throw error;
        });
    }

    return {
        fetchWithRetry,
        ajaxWithRetry,
        optimizePayload,
        processBatchReportWithRetry,
        generateIdempotencyKey,
        MAX_RETRIES,
        DEFAULT_TIMEOUT
    };
})();

if (typeof module !== 'undefined' && module.exports) {
    module.exports = ApiClient;
}
